import { useEffect, useRef } from 'react'
import {
  getJobStatus,
  isBackgroundJobActive,
  isBackgroundJobTerminal,
  MERGE_HANDLED_JOB_TYPES,
} from '@/services/jobs'
import { checkProcessedEmails } from '@/services/emailSearch'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { useDocumentStore } from '@/store/documentStore'
import { useEmailSearchStore } from '@/store/emailSearchStore'

const REFRESH_DEBOUNCE_MS = 400

/**
 * Polls all server background jobs that are still pending/running.
 * Mounted once from App. Updates the global store and runs side effects (doc list refresh, email processed info).
 */
export function useBackgroundJobsPoller() {
  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)

  const serverById = useBackgroundJobsStore((s) => s.serverById)
  const pollKey = Object.values(serverById)
    .map((r) => `${r.job.id}:${r.job.status}`)
    .sort()
    .join('|')

  const prevCompletedEmailRef = useRef<Set<number>>(new Set())
  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const needsPoll = Object.values(serverById).some((r) =>
      isBackgroundJobActive(r.job.status),
    )
    if (!needsPoll) return

    const scheduleDocumentRefresh = () => {
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current)
      }
      refreshDebounceRef.current = setTimeout(() => {
        refreshDebounceRef.current = null
        useDocumentStore.getState().triggerRefresh()
      }, REFRESH_DEBOUNCE_MS)
    }

    const tick = async () => {
      const state = useBackgroundJobsStore.getState()
      const toPoll = Object.values(state.serverById)
        .filter((r) => isBackgroundJobActive(r.job.status))
        .map((r) => r.job.id)

      for (const id of toPoll) {
        try {
          const before = useBackgroundJobsStore.getState().serverById[id]?.job
          const prevStatus = before?.status
          const updated = await getJobStatus(id)
          registerServerJob(updated)

          if (
            isBackgroundJobTerminal(updated.status) &&
            prevStatus != null &&
            !isBackgroundJobTerminal(prevStatus)
          ) {
            if (!MERGE_HANDLED_JOB_TYPES.has(updated.job_type)) {
              scheduleDocumentRefresh()
            }
          }

          if (
            updated.job_type === 'bulk_email_process' &&
            updated.status === 'completed' &&
            !prevCompletedEmailRef.current.has(updated.id)
          ) {
            prevCompletedEmailRef.current.add(updated.id)
            const { results, setProcessedInfo } = useEmailSearchStore.getState()
            if (results.length > 0) {
              try {
                const processedData = await checkProcessedEmails(
                  results.map((r) => r.message_id)
                )
                setProcessedInfo(processedData.processed)
              } catch (e) {
                console.error('checkProcessedEmails after email job:', e)
              }
            }
          }
        } catch (err) {
          console.error('Failed to poll job status:', err)
        }
      }
    }

    const interval = setInterval(() => {
      void tick()
    }, 1500)
    void tick()
    return () => clearInterval(interval)
  }, [pollKey, registerServerJob])
}
